import React, { useContext, useMemo, useState } from 'react'
// DatePicker stays raw: design-system/DateField is not graduated.
import { ConfigProvider, DatePicker } from 'antd'
import type { PickerLocale } from 'antd/es/date-picker/generatePicker'
import enUS from 'antd/locale/en_US'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'

import { SelectField } from '../../design-system/SelectField'
import { TextField } from '../../design-system/TextField'
import type { TextFieldSize } from '../../design-system/TextField'
import { Text } from '../../design-system/Text'

export interface ISelectEditorProps {
  value?: string
  options: { value: string; label: string }[]
  commit: (value: string) => void
  cancel: () => void
  /** Names the inline control for assistive tech - SelectField requires it on labelless uses. */
  ariaLabel: string
}

export const SelectEditor = ({ value, options, commit, cancel, ariaLabel }: ISelectEditorProps): React.ReactElement => (
  <SelectField
    autoFocus
    defaultOpen
    ariaLabel={ariaLabel}
    variant="borderless"
    defaultValue={value}
    options={options.map((option) => ({ ...option, title: '' }))}
    style={{ width: '100%' }}
    onSelect={(selected) => {
      if (selected === value) {
        cancel()
      } else {
        commit(selected)
      }
    }}
    onDropdownVisibleChange={(open) => {
      if (!open) {
        cancel()
      }
    }}
    onKeyDown={(event) => {
      if (event.key === 'Escape') {
        cancel()
      }
    }}
  />
)

export interface IDateEditorProps {
  value?: string
  /** The span's last day; only read when `range` is on. */
  endValue?: string
  /** Picks a span and commits once, when both ends are chosen. */
  range?: boolean
  /** The display format, or a list of it first and then what typing may parse. */
  format: string | string[]
  /** `end` is only passed in `range` mode. */
  commit: (value: string, end?: string) => void
  cancel: () => void
  clearable?: boolean
  /** Shown when a non-clearable date is emptied and submitted. */
  requiredMessage?: React.ReactNode
  /** Drawn inside the calendar panel, under it. */
  footer?: React.ReactNode
  popupClassName?: string
  /** 0 = Sunday, 1 = Monday. */
  weekStart?: number
  /** Days that cannot be picked, at either end of a span. */
  disabledDate?: (date: Dayjs) => boolean
  /** How the calendar panel opens; left out, antd's defaults apply. */
  popup?: DateEditorPopup
}

export interface DateEditorPopup {
  /** False hides the panel's "Today" shortcut. */
  showNow?: boolean
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight'
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement
  /** An empty string turns the open and close animation off. */
  transitionName?: string
}

/**
 * The picker takes its first column from the dayjs locale it names, which follows the reader's
 * language rather than the location's week. A copy of the current dayjs locale differing only in
 * `weekStart` is registered without being made global, so nothing else on the page moves.
 */
const pickerLocaleFor = (appLocale: PickerLocale | undefined, weekStart: number): PickerLocale => {
  const base = dayjs.Ls[dayjs.locale()]
  const name = `${base.name}-week${weekStart}`
  if (!dayjs.Ls[name]) {
    dayjs.locale({ ...base, name, weekStart }, undefined, true)
  }
  const picker = appLocale ?? (enUS.DatePicker as PickerLocale)
  return { ...picker, lang: { ...picker.lang, locale: name } }
}

export const DateEditor = ({
  value,
  endValue,
  range = false,
  format,
  commit,
  cancel,
  clearable = false,
  requiredMessage,
  footer,
  popupClassName,
  weekStart,
  disabledDate,
  popup,
}: IDateEditorProps): React.ReactElement => {
  const [showRequired, setShowRequired] = useState(false)
  const { locale: appLocale } = useContext(ConfigProvider.ConfigContext)
  const pickerAppLocale = appLocale?.DatePicker as PickerLocale | undefined
  const dayjsLocale = dayjs.locale()
  const pickerLocale = useMemo(
    () => (weekStart === undefined ? undefined : pickerLocaleFor(pickerAppLocale, weekStart)),
    // dayjsLocale is read inside pickerLocaleFor through dayjs.locale(), not through a closure.
    [pickerAppLocale, weekStart, dayjsLocale]
  )
  const shared = {
    autoFocus: true,
    open: true,
    variant: 'borderless' as const,
    format,
    disabledDate,
    style: { width: '100%' },
    popupClassName,
    renderExtraFooter: footer ? () => footer : undefined,
    locale: pickerLocale,
    ...popup,
    onOpenChange: (open: boolean) => {
      if (!open) {
        cancel()
      }
    },
  }

  if (range) {
    return (
      <DatePicker.RangePicker
        {...shared}
        allowClear={false}
        // Both ends or neither: a half-filled default still focuses the first input, costing a re-pick.
        defaultValue={value && endValue ? [dayjs(value), dayjs(endValue)] : undefined}
        // Both panels on the start month, so a short span is not a navigation.
        defaultPickerValue={value ? [dayjs(value), dayjs(value)] : undefined}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            cancel()
          }
        }}
        onChange={(dates) => {
          const [from, to] = dates ?? []
          if (from && to) {
            commit(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'))
          } else {
            cancel()
          }
        }}
      />
    )
  }

  return (
    <DatePicker
      {...shared}
      allowClear={clearable}
      status={showRequired ? 'error' : undefined}
      panelRender={(panel) => (
        <>
          {showRequired && (
            <div role="alert" style={{ padding: '8px 12px 0' }}>
              <Text tone="danger">{requiredMessage}</Text>
            </div>
          )}
          {panel}
        </>
      )}
      defaultValue={value ? dayjs(value) : undefined}
      onKeyDown={(event) => {
        // The picker ignores Enter on an empty input, which leaves keyboard users no way to clear
        if (value && event.key === 'Enter' && (event.target as HTMLInputElement).value === '') {
          if (clearable) {
            commit('')
          } else if (requiredMessage) {
            setShowRequired(true)
          }
        }
      }}
      onChange={(date) => {
        if (date) {
          commit(date.format('YYYY-MM-DD'))
        } else if (clearable) {
          commit('')
        } else {
          cancel()
        }
      }}
    />
  )
}

export interface ITextEditorProps {
  value?: string
  commit: (value: string) => void
  cancel: () => void
  /** Names the inline control for assistive tech - TextField requires it on labelless uses. */
  ariaLabel: string
  size?: TextFieldSize
}

export const TextEditor = ({ value, commit, cancel, ariaLabel, size }: ITextEditorProps): React.ReactElement => {
  const [draft, setDraft] = useState(value ?? '')
  const commitOrCancel = () => {
    if (draft !== (value ?? '')) {
      commit(draft)
    } else {
      cancel()
    }
  }
  return (
    <TextField
      autoFocus
      ariaLabel={ariaLabel}
      size={size}
      inputStyle="borderless"
      value={draft}
      onChange={(next) => setDraft(String(next ?? ''))}
      onBlur={commitOrCancel}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          commitOrCancel()
        }
        if (event.key === 'Escape') {
          cancel()
        }
      }}
    />
  )
}
